/**
 * Pointer positions on rotated pages.
 *
 * A page's overlay layers work in its unrotated space and are turned as a
 * group by a CSS transform (see PageView). The pointer arrives in screen
 * axes, though: offsets from an element's bounding box, and drag deltas, are
 * measured along the *displayed* page. Used as they were, a drag on a page
 * turned 90° moved text down instead of right, and a redaction box landed
 * somewhere other than where it was drawn -- and reported success.
 */

/** A point in displayed CSS pixels (from the rotated box's top-left) in
 * unrotated layer pixels. ``width``/``height`` are the layer's own size. */
export function unrotatePoint(rotation: number, x: number, y: number, width: number, height: number) {
  switch (rotation) {
    case 90:
      return { x: y, y: height - x }
    case 180:
      return { x: width - x, y: height - y }
    case 270:
      return { x: width - y, y: x }
    default:
      return { x, y }
  }
}

/** A movement on screen as a movement in the unrotated layer. */
export function unrotateDelta(rotation: number, dx: number, dy: number) {
  switch (rotation) {
    case 90:
      return { x: dy, y: -dx }
    case 180:
      return { x: -dx, y: -dy }
    case 270:
      return { x: -dy, y: dx }
    default:
      return { x: dx, y: dy }
  }
}

/** How far along a horizontal (in the layer) run a pointer is, in CSS pixels,
 * given the run element's on-screen box: the run reads right-to-left on
 * screen on a page turned 180°, top-to-bottom at 90° and bottom-to-top at 270°. */
export function alongRun(rotation: number, box: DOMRect, clientX: number, clientY: number): number {
  switch (rotation) {
    case 90:
      return clientY - box.top
    case 180:
      return box.right - clientX
    case 270:
      return box.bottom - clientY
    default:
      return clientX - box.left
  }
}
